import { useEffect, useState } from "react";
import { useAuthStore } from "../../../store/auth.store";
import { PartnerApi } from "../../../api/partner.api";
import { useTranslation } from "../../../hooks/useTranslation";
import { useNavigate } from "react-router";
import { toast } from "react-toastify";

export default function ClaimNewForm({ handleClose, opportunity }) {
  const { t } = useTranslation();
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  const token = useAuthStore((state) => state.token);
  const user = useAuthStore((state) => state.getCurrentUser());

  const [position, setPosition] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [defectDescription, setDefectDescription] = useState("");
  const [preferredSolution, setPreferredSolution] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [selectedItems, setSelectedItems] = useState({});

  // Get items from the active (default) variation
  const activeVariation = opportunity?.opportunity_variations?.find(
    (v) => v.is_default
  ) || opportunity?.opportunity_variations?.[0];
  const items = activeVariation?.items || [];

  useEffect(() => {
    if (user) {
      setPhone(user.current_person?.phone || "");
      setEmail(user.current_person?.email || user.email || "");
      setPosition(user.current_person?.position || "");
    }
  }, [user]);

  const toggleItem = (itemId) => {
    setSelectedItems((prev) => {
      const newItems = { ...prev };
      if (newItems[itemId]) {
        delete newItems[itemId];
      } else {
        const item = items.find((i) => i.item_id === itemId);
        newItems[itemId] = item ? item.quantity : 1;
      }
      return newItems;
    });
  };

  const toggleAll = () => {
    if (Object.keys(selectedItems).length === items.length) {
      setSelectedItems({});
    } else {
      const all = {};
      items.forEach((item) => {
        all[item.item_id] = item.quantity;
      });
      setSelectedItems(all);
    }
  };

  const setItemQuantity = (itemId, quantity) => {
    setSelectedItems((prev) => ({
      ...prev,
      [itemId]: parseInt(quantity) || 1,
    }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setError(null);

    if (Object.keys(selectedItems).length === 0) {
      setError(t("claims.selectItems"));
      return;
    }

    setSubmitting(true);

    const claimItems = Object.entries(selectedItems).map(
      ([item_id, quantity]) => ({
        item_id: parseInt(item_id),
        quantity,
      })
    );

    PartnerApi.claimAdd({
      token,
      data: {
        opportunity_id: opportunity.opportunity_id,
        position,
        phone,
        email,
        defect_description: defectDescription,
        preferred_solution: preferredSolution,
        items: claimItems,
        attachments,
      },
    }).then((result) => {
      setSubmitting(false);
      if (result && "error" in result) {
        setError(result.error);
        return;
      }
      if (result && result.claim_id) {
        toast.success(t("claims.claimCreated"));
        handleClose();
        navigate("/reklamace/" + result.claim_id);
      }
    }).catch(() => {
      setSubmitting(false);
      setError(t("common.error"));
    });

    return false;
  };

  const applicantName =
    (user?.current_person?.first_name || "") +
    " " +
    (user?.current_person?.last_name || "");

  return (
    <form onSubmit={(e) => handleSubmit(e)}>
      {error && <p className="alert alert-danger">{error}</p>}

      <h5 className="mb-4">{t("claims.applicant")}</h5>
      <div className="row mb-5">
        <div className="col-md-6 mb-3">
          <label className="form-label">{t("claims.applicantName")}</label>
          <input
            type="text"
            className="form-control form-control-solid"
            value={applicantName}
            readOnly
          />
        </div>
        <div className="col-md-6 mb-3">
          <label className="form-label">{t("claims.position")}</label>
          <input
            type="text"
            className="form-control form-control-solid"
            value={position}
            onChange={(e) => setPosition(e.target.value)}
          />
        </div>
        <div className="col-md-6 mb-3">
          <label className="form-label">{t("claims.phone")}</label>
          <input
            type="text"
            className="form-control form-control-solid"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
          />
        </div>
        <div className="col-md-6 mb-3">
          <label className="form-label">{t("claims.email")}</label>
          <input
            type="email"
            className="form-control form-control-solid"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
      </div>

      <h5 className="mb-4">{t("claims.supplier")}</h5>
      <div className="mb-5">
        <input
          type="text"
          className="form-control form-control-solid"
          value="Exclusive Networks Czechia s. r. o."
          readOnly
        />
      </div>

      <h5 className="mb-4">{t("claims.subjectOfClaim")}</h5>
      <div className="row mb-3">
        <div className="col-md-6 mb-3">
          <label className="form-label">{t("claims.orderNumber")}</label>
          <input
            type="text"
            className="form-control form-control-solid"
            value={opportunity?.partner_order_id || "-"}
            readOnly
          />
        </div>
        <div className="col-md-6 mb-3">
          <label className="form-label">{t("claims.invoiceNumber")}</label>
          <input
            type="text"
            className="form-control form-control-solid"
            value={
              opportunity?.invoices?.map((i) => i.invoice_number).join(", ") ||
              "-"
            }
            readOnly
          />
        </div>
      </div>

      <div className="mb-5">
        <label className="form-label">{t("claims.selectItems")}</label>
        <div className="table-responsive">
          <table className="table table-bordered table-sm">
            <thead>
              <tr>
                <th className="w-30px">
                  <div className="form-check">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={
                        items.length > 0 &&
                        Object.keys(selectedItems).length === items.length
                      }
                      onChange={toggleAll}
                    />
                  </div>
                </th>
                <th>{t("opportunities.product")}</th>
                <th>SKU</th>
                <th>{t("claims.quantity")}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.item_id}>
                  <td>
                    <div className="form-check">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={!!selectedItems[item.item_id]}
                        onChange={() => toggleItem(item.item_id)}
                      />
                    </div>
                  </td>
                  <td>{item.product.name}</td>
                  <td>
                    <span className="badge badge-info badge-outline">
                      {item.product.sku}
                    </span>
                  </td>
                  <td className="w-100px">
                    <input
                      type="number"
                      className="form-control form-control-sm"
                      min="1"
                      max={item.quantity}
                      value={selectedItems[item.item_id] || ""}
                      disabled={!selectedItems[item.item_id]}
                      onChange={(e) =>
                        setItemQuantity(item.item_id, e.target.value)
                      }
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mb-5">
        <label className="required form-label">
          {t("claims.defectDescription")}
        </label>
        <textarea
          className="form-control form-control-solid"
          rows="4"
          required
          value={defectDescription}
          onChange={(e) => setDefectDescription(e.target.value)}
        />
      </div>

      <div className="mb-5">
        <label className="required form-label">
          {t("claims.preferredSolution")}
        </label>
        <textarea
          className="form-control form-control-solid"
          rows="3"
          required
          value={preferredSolution}
          onChange={(e) => setPreferredSolution(e.target.value)}
        />
      </div>

      <div className="mb-5">
        <label className="form-label">{t("claims.addAttachments")}</label>
        <input
          type="file"
          className="form-control form-control-solid"
          multiple
          onChange={(e) => setAttachments(Array.from(e.target.files))}
        />
        {attachments.length > 0 && (
          <div className="mt-2">
            <small className="text-muted">
              {t("claims.selectedFiles")}:{" "}
              {attachments.map((f) => f.name).join(", ")}
            </small>
          </div>
        )}
      </div>

      <button
        type="submit"
        className="btn btn-primary"
        disabled={submitting}
      >
        {submitting ? t("common.uploading") : t("claims.submitClaim")}
      </button>
    </form>
  );
}
